import countries from 'i18n-iso-countries';
import en from 'i18n-iso-countries/langs/en.json';

countries.registerLocale(en);

const countryMap = countries.getNames('en');

const sortedCountryEntries = Object.entries(countryMap).sort(([codeA], [codeB]) => codeA.localeCompare(codeB));

const isoCountries = sortedCountryEntries.reduce((acc, [code, name]) => {
  acc[code] = name;
  return acc;
}, {});

const countryNameList = [];
const countryCodeList = [];
const countryList = [];
const countryAlpha2List = [];

const FrenchTerritoriesAlpha2 = ['FR', 'GP', 'GF', 'RE', 'MQ', 'YT', 'NC', 'PF', 'BL', 'MF', 'PM', 'TF', 'WF'];

// Monaco also uses the French SIRET/SIREN, so the field is displayed there, but it is optional.
const SiretCountriesAlpha2 = [...FrenchTerritoriesAlpha2, 'MC'];

// A SIRET is the 9-digit SIREN followed by its 5-digit NIC.
export const SIREN_LENGTH = 9;
export const SIRET_LENGTH = 14;

// Country a business identity field is validated against: billing first, main address as fallback,
// as the back end does. Shared by the verified VAT, the SIRET and the EIN rules.
// When the billing address mirrors the main address ("Use as Billing Address"), the billing country
// is ignored: the form keeps the previously entered billing values, which would otherwise stay in
// effect after the toggle.
export const getBusinessCountryCode = (address, billingAddress, useAsBillingAddress) => {
  if (useAsBillingAddress) {
    return address && address.countryCode;
  }

  return (billingAddress && billingAddress.countryCode) || (address && address.countryCode);
};

export const getCountryName = alpha2 => {
  if (typeof alpha2 !== 'string' || alpha2.length !== 2) {
    return '';
  }

  return isoCountries[alpha2] || '';
};

// Countries where the SIRET/SIREN field is displayed (and saved).
export const isSiretCountry = alpha2 => SiretCountriesAlpha2.includes(alpha2);

// SIRET/SIREN is mandatory for mainland France only. It stays optional for Monaco and the
// French overseas territories (field displayed, but empty is allowed).
export const isSiretRequired = alpha2 => alpha2 === 'FR';

// The SIREN is the first 9 digits of a SIRET/SIREN value.
export const getSiren = siretSiren => String(siretSiren).slice(0, SIREN_LENGTH);

// Keeps the electronic address (BT-49) in sync with the SIRET/SIREN: the leading SIREN and an
// embedded 14-digit SIRET are re-derived, while a routing code or free suffix is preserved.
export const deriveElectronicAddress = (prevAddress, siretSiren) => {
  if (!siretSiren) {
    return '';
  }

  const siren = getSiren(siretSiren);

  if (!prevAddress) {
    return siren;
  }

  const parts = String(prevAddress).split('_');
  parts[0] = siren;

  // A 14-digit second token is a copy of the SIRET: re-derive it, or collapse to the
  // bare SIREN when the SIRET/SIREN no longer holds a full SIRET.
  if (parts[1] && /^\d{14}$/.test(parts[1])) {
    if (String(siretSiren).length !== SIRET_LENGTH) {
      return siren;
    }
    parts[1] = String(siretSiren);
  }

  return parts.join('_');
};

/**
 * Visually separates the SIREN (first 9 digits) from the remaining SIRET digits (NIC).
 * Shorter values occur while typing, so they are returned unseparated.
 * @param {string} value raw SIRET digits
 * @returns {string} e.g. "732829320 00074" (empty string when there is no value)
 */
export const formatSiretSiren = value => {
  if (!value) {
    return '';
  }

  const digits = String(value);

  if (digits.length <= SIREN_LENGTH) {
    return digits;
  }

  return `${digits.slice(0, SIREN_LENGTH)} ${digits.slice(SIREN_LENGTH)}`;
};

/**
 * Translates a caret offset counted in digits into its offset in the formatted value, so an edit
 * inside the SIREN can put the caret back where the user left it instead of at the end.
 * @param {number} digitCaret number of digits before the caret
 * @returns {number} caret offset in the value returned by formatSiretSiren
 */
export const getSiretSirenCaretPosition = digitCaret => (digitCaret > SIREN_LENGTH ? digitCaret + 1 : digitCaret);

sortedCountryEntries.forEach(([code, name]) => {
  countryNameList.push(name);
  countryCodeList.push(code);
  countryList.push({
    id: name,
    name,
  });
  countryAlpha2List.push({
    id: code,
    name,
  });
});

export default isoCountries;
export { countryNameList, countryCodeList, countryList, countryAlpha2List };
